import { classify } from 'sidecar-utilities'

import { Sentiment } from '@sidecar-components/types'

export interface CardStyleProps {
  isClickable?: boolean
  sentiment?: Sentiment
}
export const cardStyles = ({ isClickable, sentiment }: CardStyleProps) =>
  classify({
    // block is needed because Links are inherently inline
    display: 'block',
    margin: 'mb-4 last:mb-0',
    defaultBg: sentiment === 'default' ? 'color-bgDefaultContainer' : null,
    accentBg: sentiment === 'accent' ? 'color-background-accent' : null,
    brandBg: sentiment === 'brand' ? 'color-background-brand' : null,
    notifyBg: sentiment === 'notify' ? 'color-background-notify' : null,
    riskBg: sentiment === 'risk' ? 'color-background-risk' : null,
    weakBg: sentiment === 'weak' ? 'color-background-weak' : null,
    clickableBg: isClickable
      ? 'group/card hover:color-bgDefaultContainer-hover active:color-bgDefaultContainer-active focus:color-bgDefaultContainer-hover'
      : null,
    border: 'elevation-1st-floor rounded-lg',
    animate: 'animate-fade-in',
  })

export interface contentStyleProps {
  hasBgShapes?: boolean
  hasFooter?: boolean
  hasList?: boolean
  isClickable?: boolean
  sentiment?: Sentiment
}
export const contentStyles = ({
  hasBgShapes,
  hasFooter,
  hasList,
  isClickable,
  sentiment,
}: contentStyleProps) =>
  classify({
    width: 'w-full relative',
    // if the card has a list, we want to remove the padding on the right, top, and bottom
    paddingY: hasList ? `py-0` : `py-5 md:py-5 lg:py-6`,
    paddingX: hasList ? 'pr-0 pl-5 md:pl-6 lg:pl-8' : 'px-5 sm:px-6 lg:px-8',
    // if the card is clickable, we need a gutter for the right chevron
    clickableGap: isClickable ? 'flex gap-5 md:gap-6 lg:gap-8' : null,
    border: hasFooter ? 'rounded-t-lg' : 'rounded-lg',
    shapeCropping: hasBgShapes ? 'overflow-hidden' : null,
    responsiveBgShapes: hasBgShapes ? '@container/card' : null,
    font: `[&_p.subtitle]:sh-body [&_p.subtitleSmall]:sh-body-sm`,
    defaultChildren:
      sentiment === 'default'
        ? `[&_p.inherit]:color-textOnDefault
         [&_svg.inherit]:color-icon-default
         [&_svg.inherit.color-icon-weak-default]:color-icon-weak-default
         [&_thead]:color-bgDefaultContainer
         [&_table_thead_th:first-child]:after:color-bgDefaultContainer
         [&_table_thead_th:last-child]:after:color-bgDefaultContainer`
        : null,
    accentChildren:
      sentiment === 'accent'
        ? `[&_h3]:color-textOnAccent-heavy-default
         [&_h4]:color-textOnAccent-bold-default
         [&_p]:color-textOnAccent-default
         [&_p.sh-body-sm]:color-textOnAccent-weak-default
         [&_.sh-ul]:color-textOnAccent-default
         [&_.sh-ol]:color-textOnAccent-default
         [&_.sh-priceShieldlist]:color-textOnAccent-default
         [&_strong]:color-textOnAccent-bold-default
         [&_svg.inherit]:color-iconOnAccent-default
         [&_svg.inherit.color-icon-weak-default]:color-iconOnAccent-weak-default
         [&_thead]:color-background-accent
         [&_table_thead_th:first-child]:after:color-background-accent
         [&_table_thead_th:last-child]:after:color-background-accent`
        : null,
    brandChildren:
      sentiment === 'brand'
        ? `[&_h3]:color-textOnBrand-heavy-default
         [&_h4]:color-textOnBrand-bold-default
         [&_p]:color-textOnBrand-default
         [&_p.sh-body-sm]:color-textOnBrand-weak-default
         [&_.sh-ul]:color-textOnBrand-default
         [&_.sh-ol]:color-textOnBrand-default
         [&_.sh-priceShieldlist]:color-textOnBrand-default
         [&_strong]:color-textOnBrand-bold-default
         [&_svg.inherit]:color-iconOnBrand-default
         [&_svg.inherit.color-icon-weak-default]:color-iconOnBrand-weak-default
         [&_thead]:color-background-brand
         [&_table_thead_th:first-child]:after:color-background-brand
         [&_table_thead_th:last-child]:after:color-background-brand
         [&_a]:color-textEngageOnBrand-default
         [&_a:hover]:color-textEngageOnBrand-hover
         [&_a:active]:color-textEngageOnBrand-active
         [&_a:focus]:color-textEngageOnBrand-hover`
        : null,
    notifyChildren:
      sentiment === 'notify'
        ? `[&_h3]:color-textOnNotify-heavy-default
         [&_h4]:color-textOnNotify-bold-default
         [&_p]:color-textOnNotify-default
         [&_p.sh-body-sm]:color-textOnNotify-weak-default
         [&_.sh-ul]:color-textOnNotify-default
         [&_.sh-ol]:color-textOnNotify-default
         [&_.sh-priceShieldlist]:color-textOnNotify-default
         [&_strong]:color-textOnNotify-bold-default
         [&_svg.inherit]:color-iconOnNotify-default
         [&_svg.inherit.color-icon-weak-default]:color-iconOnNotify-weak-default
         [&_thead]:color-background-notify
         [&_table_thead_th:first-child]:after:color-background-notify
         [&_table_thead_th:last-child]:after:color-background-notify`
        : null,
    riskChildren:
      sentiment === 'risk'
        ? `[&_h3]:color-textOnRisk-heavy-default
         [&_h4]:color-textOnRisk-bold-default
         [&_p]:color-textOnRisk-default
         [&_p.sh-body-sm]:color-textOnRisk-weak-default
         [&_.sh-ul]:color-textOnRisk-default
         [&_.sh-ol]:color-textOnRisk-default
         [&_strong]:color-textOnRisk-bold-default
         [&_svg.inherit]:color-iconOnRisk-default
         [&_svg.inherit.color-icon-weak-default]:color-iconOnRisk-weak-default
         [&_thead]:color-background-risk
         [&_table_thead_th:first-child]:after:color-background-risk
         [&_table_thead_th:last-child]:after:color-background-risk`
        : null,
    weakChildren:
      sentiment === 'weak'
        ? `[&_h3]:color-textOnDefaultStrong
         [&_h4]:color-textOnDefaultStrong
         [&_p]:color-textOnDefault
         [&_p.sh-body-sm]:color-textOnDefaultWeak
         [&_svg.inherit]:color-icon-default
         [&_svg.inherit.color-icon-weak-default]:color-icon-weak-default
         [&_thead]:color-background-weak
         [&_table_thead_th:first-child]:after:color-background-weak
         [&_table_thead_th:last-child]:after:color-background-weak`
        : null,
  })

export const childrenStyles = hasBgShapes =>
  classify({
    width: 'w-full',
    // make sure the content is on top of the background shapes
    z: hasBgShapes ? 'relative z-1' : null,
  })

export const cardLinkChevronStyles = hasBgShapes =>
  classify({
    tag: 'cardLinkChevron',
    color: `color-icon-weak-default
  group-hover/card:color-icon-weak-hover
  group-active/card:color-icon-weak-active`,
    layout: 'self-center',
    // make sure the content is on top of the background shapes
    z: hasBgShapes ? 'relative z-1' : null,
  })

export const footerStyles = classify({
  background: 'color-bgDefaultStrong',
  padding: 'py-5 px-5 sm:px-6 lg:px-8',
  border: 'border-t border-solid color-border-weak-default rounded-b-lg',
})

const bgShapeBase = 'absolute h-auto'

export const bgCrossStyles = classify({
  bgShapeBase,
  // The cross gets bigger when the card (not screen) gets bigger
  width: 'w-28 @md/card:w-36',
  top: '-top-10  @md/card:-top-12',
  left: '-left-10 @md/card:-left-14',
})

export const bgWheelStyles = classify({
  bgShapeBase,
  // The wheel gets bigger when the card (not screen) gets bigger
  width: 'w-28 @md/card:w-48',
  bottom: '-bottom-11 @md/card:-bottom-18',
  right: '-right-8 @md/card:-right-18',
})
